import React from 'react';
import styled, { css } from 'styled-components';
import { Icons, ImpIcon, ImpSpinner } from '../..';
import { colors } from '../../DesignSystem/colors';
import { TYPOGRAPHY } from '../../DesignSystem/typography';
import { ANIMATION_DURATION, Breakpoints, MaxWidth, shimmerCSS, } from './../../DesignSystem/design-system';
const BUTTON_DEFAULT_HEIGHT = '48px';
const BUTTON_DEFAULT_WIDTH = `min(calc(100vw - 48px), ${MaxWidth.Button})`;
const BUTTON_MIN_WIDTH = '96px';
const BUTTON_DISABLED = ['disabled', 'loading', 'submitting'];
const getCursor = (state) => {
    switch (state) {
        case 'disabled':
        case 'submitting':
            return 'not-allowed';
        case 'loading':
            return 'default';
        case 'enabled':
            return 'pointer';
    }
};
const getHoverEffect = (variant) => {
    switch (variant) {
        case 'inline':
            return css `
        @media (hover: hover) {
          &:hover {
            text-decoration: none;
          }
        }
        @media (hover: none) {
          &:active {
            text-decoration: none;
            opacity: 0.8;
          }
        }
      `;
        case 'wrapper':
            return;
        default:
            return css `
        @media (hover: hover) {
          &:hover {
            opacity: 0.8;
          }
        }
        @media (hover: none) {
          &:active {
            opacity: 0.8;
          }
        }
      `;
    }
};
const getMainColor = (buttonStyle, color) => {
    switch (buttonStyle) {
        case 'imprint':
            return colors.content.onBackground;
        case 'branded':
            return `var(--merchant-color, ${colors.content.onBackground})`;
        case 'color':
            return color || colors.content.onBackground;
        default:
            return colors.content.onBackground;
    }
};
const getTextColor = (buttonStyle, buttonVariant, color, isDisabled) => {
    if (isDisabled) {
        return colors.content.onBackgroundDisabled;
    }
    if (buttonVariant === 'primary') {
        return colors.background.backgroundPrimary;
    }
    return getMainColor(buttonStyle, color);
};
const getBorderRadius = (buttonVariant) => {
    switch (buttonVariant) {
        case 'inline':
        case 'wrapper':
            return '0px';
        default:
            return '24px';
    }
};
const Button = styled.button `
  cursor: ${({ state }) => getCursor(state || 'enabled')};
  ${TYPOGRAPHY.label2.desktop};
  border-radius: ${({ variant }) => getBorderRadius(variant || 'primary')};
  border: 1px solid;
  position: relative;
  transition: ${ANIMATION_DURATION} all;
  padding: 0px;

  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;

  ${({ variant, buttonStyle, color }) => {
    switch (variant) {
        case 'primary':
            return `
          color: ${colors.background.backgroundPrimary};
          background-color: ${getMainColor(buttonStyle, color)};
          border: none;
          height: ${BUTTON_DEFAULT_HEIGHT};
          width: ${BUTTON_DEFAULT_WIDTH};
        `;
        case 'secondary':
            return `
          color: ${getMainColor(buttonStyle, color)};
          background-color: transparent;
          border-color: ${getMainColor(buttonStyle, color)};
          height: ${BUTTON_DEFAULT_HEIGHT};
          width: ${BUTTON_DEFAULT_WIDTH};
        `;
        case 'tertiary':
            return `
            color: ${getMainColor(buttonStyle, color)};
            background-color: transparent;
            border: none;
            height: ${BUTTON_DEFAULT_HEIGHT};
            width: ${BUTTON_DEFAULT_WIDTH};
          `;
        case 'text':
            return `
          color: ${getMainColor(buttonStyle, color)};
          background-color: transparent;
          border: none;
          height: auto;
          width: auto;
        `;
        case 'inline':
            return `
          display: inline-block;
          color: ${getMainColor(buttonStyle, color)};
          border: none;
          text-decoration: underline;
          width: auto;
          height: auto;
          background-color: transparent;
          padding: 0px!important;
          font-size: inherit!important;
          line-height: inherit!important;
      `;
        case 'wrapper':
            return `
          color: ${getMainColor(buttonStyle, color)};
          border: none;
          border-radius: 0px;
          background-color: transparent;
          text-decoration: none;
          display: flex;
          width: auto;
      `;
    }
}}
  ${({ size }) => {
    switch (size) {
        case 'hug':
            return `
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0 32px;
          width: auto;
          overflow: hidden;
          min-width: ${BUTTON_MIN_WIDTH};
        `;
        case 'fill':
            return `
          width: 100%;
        `;
    }
}}
  ${({ state, variant }) => {
    switch (state) {
        case 'submitting':
            return `
          color: transparent!important;
        `;
        case 'loading':
            return css `
          border: none;
          background-color: ${colors.background.backgroundPrimary};
          ${shimmerCSS}
        `;
        case 'disabled':
            switch (variant) {
                case 'primary':
                    return `
              background-color: ${colors.surface.surfaceDisabled};
              color: ${colors.content.onBackgroundDisabled};
              border-color: transparent;
            `;
                case 'secondary':
                    return `
              background-color: transparent;
              color: ${colors.content.onBackgroundDisabled};
              border-color:${colors.border.borderDisabled};
            `;
                default:
                    return `
              color: ${colors.content.onBackgroundDisabled};
            `;
            }
            return;
        case 'enabled':
            return `
            ${getHoverEffect(variant || 'primary')}
            &:active {
              opacity: 0.6;
            }
        `;
    }
}}

  @media (max-width: ${Breakpoints.Mobile}) {
    ${TYPOGRAPHY.label2.mobile}
  }
`;
const SpinnerContainer = styled.div `
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
/**
 * ImpButton is a button component used to trigger actions. We can use merchant color or Imprint's colors, and show multiple states.
 *
 * @component
 * @example
 * <ImpButton onClick={() => console.log("Button clicked")}>
 *   Submit
 * </ImpButton>
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The content of the button.
 * @param {ImpButtonVariant} [props.variant="primary"] - The button variant ("primary", "secondary", "text").
 * @param {ImpButtonStyle} [props.buttonStyle="imprint"] - The button style ("imprint", "branded", "color").
 * @param {ImpButtonSize} [props.size="m"] - The button size ("hug", "fill", "m").
 * @param {ImpButtonState} [props.state="enabled"] - The button state ("loading", "submitting", "disabled", "enabled").
 * @param {color} - Custom main color for the button (necessary for color button style to work correctly)
 * @param {IconComponentType} [props.leadingIcon=undefined] - Icon that appears in front of the text
 * @param {IconComponentType} [props.trailingIcon=undefined] - Icon that appears at the end of the text
 *
 * @returns {JSX.Element} The rendered component.
 */
export default function ImpButton({ children, variant = 'primary', buttonStyle = 'imprint', size = 'm', state = 'enabled', tabIndex = 0, color = 'red', dataTestId = 'button', linkOut = false, leadingIcon = undefined, trailingIcon = undefined, ...props }) {
    const isButtonDisabled = BUTTON_DISABLED.includes(state);
    return (React.createElement(Button, { variant: variant, buttonStyle: buttonStyle, size: size, state: state, disabled: isButtonDisabled, "aria-disabled": isButtonDisabled, tabIndex: isButtonDisabled ? -1 : tabIndex, color: color, "data-testid": dataTestId, ...props },
        leadingIcon && (React.createElement(ImpIcon, { size: "20px", iconSrc: leadingIcon, stroke: color })),
        children,
        trailingIcon && (React.createElement(ImpIcon, { size: "20px", iconSrc: trailingIcon, stroke: color })),
        linkOut && !(state === 'loading' || state === 'submitting') && (React.createElement(ImpIcon, { size: "20px", iconSrc: Icons.arrowSquareOut, stroke: getTextColor(buttonStyle, variant, color, isButtonDisabled) })),
        state === 'submitting' && (React.createElement(SpinnerContainer, null,
            React.createElement(ImpSpinner, { size: 'm', color: getSpinnerColor(variant, buttonStyle, color) })))));
}
function getSpinnerColor(variant, buttonStyle, color) {
    switch (variant) {
        case 'primary':
            return colors.content.onBackgroundInverse;
        case 'secondary':
        case 'tertiary':
        case 'text':
            return getMainColor(buttonStyle, color);
        default:
            return colors.background.backgroundPrimary;
    }
}
//# sourceMappingURL=ImpButton.js.map